import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Form,
  Input,
  Label,
  Button,
  FormFeedback,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useContext } from "react";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { REGEX, ROLE_BASED_DEFAULT_ROUTE} from "constants/CommonConstants";
import { encodePassword } from "components/CommonComponents/encodeDecodeFunction";

const SetPassword = ({ isOpen, toggleModal, onSubmit }) => {
  const navigate = useNavigate();
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const { notify } = useContext(NotificationContext);
  const [form, setForm] = useState({
    password: { value: "", error: "" },
    confirmPassword: { value: "", error: "" },
  });

  const [ui, setUI] = useState({
    loading: false,
  });

  const [visibility, setVisibility] = useState({
    password: false,
    confirmPassword: false,
  });

  // Reset everything when modal closes
  useEffect(() => {
    if (!isOpen) {
      setForm({
        password: { value: "", error: "" },
        confirmPassword: { value: "", error: "" },
      });
      setUI({ loading: false });
      setVisibility({ password: false, confirmPassword: false });
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
        error: "",
      },
    }));
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    switch (name) {
      case "password":
        if (!trimmedValue) return "Password is required";

        if (!REGEX.PASSWORD_REGEX.test(trimmedValue))
          return "Please enter a strong password";

        return "";

      case "confirmPassword":
        if (!trimmedValue) return "Confirm password is required";

        if (trimmedValue !== form.password.value)
          return "Passwords do not match";

        return "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    let updatedForm = { ...form };
    let isValid = true;

    Object.keys(updatedForm).forEach((key) => {
      const error = getValidationError(key, updatedForm[key].value);

      updatedForm[key] = {
        ...updatedForm[key],
        error,
      };

      if (error) isValid = false;
    });

    setForm(updatedForm);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUI((prev) => ({ ...prev, apiError: "" }));

    if (!validateForm()) return;

    const defaultRoute = ROLE_BASED_DEFAULT_ROUTE[role] || "/";

    try {
      setUI((prev) => ({ ...prev, loading: true }));

      const res = await onSubmit({
        newPassword: encodePassword(form.password.value),
        confirmPassword: encodePassword(form.confirmPassword.value),
      });
    } catch (error) {
      notify("danger", error?.message || "Something went wrong");
    } finally {
      setUI((prev) => ({ ...prev, loading: false }));
      navigate(defaultRoute);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => {}}
      backdrop="static"
      keyboard={false}
      // size="md"
      className="modal-dialog-centered"
    >
      <ModalHeader>Set Password</ModalHeader>

      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Password */}
          <Label>New Password</Label>
          <InputGroup>
            <Input
              type={visibility.password ? "text" : "password"}
              name="password"
              value={form.password.value}
              onChange={handleChange}
              invalid={!!form.password.error}
              placeholder="Enter new password"
            />
            <InputGroupText
              className="cursor"
              onClick={() =>
                setVisibility((prev) => ({
                  ...prev,
                  password: !prev.password,
                }))
              }
            >
              <i
                className={`fa ${
                  visibility.password ? "fa-eye-slash" : "fa-eye"
                }`}
              />
            </InputGroupText>
            <FormFeedback>{form.password.error}</FormFeedback>
          </InputGroup>

          {/* Confirm Password */}
          <Label className="mt-4">Confirm Password</Label>
          <InputGroup>
            <Input
              type={visibility.confirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={form.confirmPassword.value}
              onChange={handleChange}
              invalid={!!form.confirmPassword.error}
              placeholder="Confirm password"
            />
            <InputGroupText
              className="cursor"
              onClick={() =>
                setVisibility((prev) => ({
                  ...prev,
                  confirmPassword: !prev.confirmPassword,
                }))
              }
            >
              <i
                className={`fa ${
                  visibility.confirmPassword ? "fa-eye-slash" : "fa-eye"
                }`}
              />
            </InputGroupText>
            <FormFeedback>{form.confirmPassword.error}</FormFeedback>
          </InputGroup>

          {/* Submit */}
          <div className="text-end mt-5">
            <Button color="deep-blue" type="submit" disabled={ui.loading}>
              {ui.loading ? (
                <i className="fa fa-spinner fa-spin fa-lg text-white" />
              ) : (
                "Set Password"
              )}
            </Button>
          </div>
        </ModalBody>
      </Form>
    </Modal>
  );
};

export default SetPassword;
